import React, { useState, useEffect } from 'react';
import { Language_Text_Editor } from './language_text_editor.js';
import { FaCheck } from 'react-icons/fa';
import { Colors } from './form_builder.js';
import { useTranslation } from 'react-i18next';
import * as menu from "../../../assets/menus/menu.js";

/**
 * Answer_Editor
 *
 * Props:
 * - answer: { answerCode: string, answerText: [], score: number | null }
 * - language: string (ISO code)
 * - onConfirm: function(updatedAnswer)
 */
const Answer_Editor = ({ 
  answer, 
  language, 
  onConfirm,
  colors = Colors,
  isEditing = false,
  parentEditContext = null
}) => {
  const [localAnswer, setLocalAnswer] = useState({
    answerCode: '',
    answerText: [],
    score: null,
    invisible: false,
    notApplicable: false
  });
  const { t } = useTranslation();

  useEffect(() => {
    setLocalAnswer({
      answerCode: answer.answerCode || '',
      answerText: answer.answerText || [],
      score: answer.score ?? null,
      invisible: answer.invisible ?? false,
      notApplicable: answer.notApplicable ?? false
    });
  }, [answer]);

  // Labels are now managed centrally in menu.js via form_builder_labels.js
  // No need to set up individual labels here as they are loaded automatically

  const handleConfirm = () => {
    onConfirm(localAnswer);
  };

  const handleScoreChange = (e) => {
    const value = e.target.value;
    setLocalAnswer(prev => ({
      ...prev,
      score: value === '' ? null : parseFloat(value)
    }));
  };

  // Specific edit context colors for answers
  const editContextColors = colors.editContextAnswer;

  return (
    <div className="card shadow w-100" style={{ borderColor: editContextColors.border, borderWidth: '2px' }}>
      <div className="card-header d-flex justify-content-between align-items-center"
          style={{ backgroundColor: editContextColors.header, color: colors.white }}>
        <h5 className="mb-0">
          <span className="badge bg-white text-dark me-2">{t('editingAnswer', { ns: 'labels' })}</span>
          {localAnswer.answerText.find(t => t.language === language)?.text || localAnswer.answerCode || t('newAnswer', { ns: 'labels' })}
        </h5>
        <button className="btn btn-sm" 
            style={{ backgroundColor: colors.white, color: colors.dark }}
            onClick={handleConfirm}>
          <FaCheck className="me-1" />
          {t('done', { ns: 'labels' })}
        </button>
      </div>

      <div className="card-body p-4" style={{ backgroundColor: editContextColors.body }}>
        <div className="row g-3 w-100 mx-0">
          <div className="col-12 px-0">
            <div className="form-group w-100">
              <label className="form-label fw-bold">{t('answerCode', { ns: 'labels' })}</label>
              <input
                type="text"
                className="form-control w-100"
                value={localAnswer.answerCode}
                onChange={(e) =>
                  setLocalAnswer(prev => ({ ...prev, answerCode: e.target.value }))
                }
                placeholder={t('uniqueIdentifier', { ns: 'labels' })}
              />
              <div className="form-text text-muted">
                {t('uniqueIdentifier', { ns: 'labels' })}
              </div>
            </div>
          </div>

          <div className="col-12 px-0 mb-3">
            <div className="form-check">
              <input 
                className="form-check-input" 
                type="checkbox" 
                id="answerInvisible"
                checked={localAnswer.invisible}
                onChange={(e) => setLocalAnswer(prev => ({ ...prev, invisible: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="answerInvisible">
                {t('invisible', { ns: 'labels' })}
              </label>
            </div>
            <div className="form-check mt-2">
              <input 
                className="form-check-input" 
                type="checkbox" 
                id="answerNotApplicable"
                checked={localAnswer.notApplicable}
                onChange={(e) => setLocalAnswer(prev => ({ ...prev, notApplicable: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="answerNotApplicable">
                {t('notApplicable', { ns: 'labels', defaultValue: 'Considera non applicabile' })}
              </label>
              <div className="form-text text-muted small">
                {t('notApplicableHelp', { ns: 'labels', defaultValue: 'Se selezionato, questa risposta verrà esclusa dal calcolo della conformità' })}
              </div>
            </div>
          </div>

          <div className="col-12 px-0">
            <div className="form-group w-100">
              <label className="form-label fw-bold">{t('answerText', { ns: 'labels' })} ({language})</label>
              <Language_Text_Editor
                language={language}
                mode="textarea"
                text={localAnswer.answerText}
                onChange={(updatedText) =>
                  setLocalAnswer(prev => ({ ...prev, answerText: updatedText }))
                }
                colors={colors}
              />
            </div>
          </div>

          <div className="col-md-6 px-0">
            <div className="form-group w-100">
              <label className="form-label fw-bold">{t('score', { ns: 'labels' })}</label>
              <input
                type="number"
                step="0.01"
                className="form-control w-100"
                value={localAnswer.score ?? ''}
                onChange={handleScoreChange}
                placeholder={t('enterNumericValue', { ns: 'labels' })}
              />
              <div className="form-text text-muted">
                {t('numericValue', { ns: 'labels' })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { Answer_Editor };
